import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const ProgressContext = createContext(null);

export function ProgressProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]); // [{category_slug, prompt_index}]

  const refresh = useCallback(async () => {
    if (!user || typeof user !== "object") return;
    try {
      const { data } = await api.get("/progress");
      setItems(data);
    } catch {
      setItems([]);
    }
  }, [user]);

  useEffect(() => {
    if (user && typeof user === "object") refresh();
    else setItems([]);
  }, [user, refresh]);

  const isDone = useCallback(
    (slug, idx) => items.some((i) => i.category_slug === slug && i.prompt_index === idx),
    [items]
  );

  const countFor = useCallback(
    (slug) => items.filter((i) => i.category_slug === slug).length,
    [items]
  );

  const toggle = useCallback(async (slug, idx) => {
    const currently = items.some((i) => i.category_slug === slug && i.prompt_index === idx);
    if (currently) {
      setItems((prev) => prev.filter((i) => !(i.category_slug === slug && i.prompt_index === idx)));
      try { await api.delete(`/progress/${slug}/${idx}`); } catch { refresh(); }
    } else {
      setItems((prev) => [...prev, { category_slug: slug, prompt_index: idx }]);
      try { await api.post("/progress", { category_slug: slug, prompt_index: idx }); } catch { refresh(); }
    }
  }, [items, refresh]);

  return (
    <ProgressContext.Provider value={{ items, isDone, countFor, toggle, refresh }}>
      {children}
    </ProgressContext.Provider>
  );
}

export const useProgress = () => useContext(ProgressContext);
